import { useEffect } from 'react';
import { atendimentoApi } from './atendimento-api';
import { useSessao } from './sessao';

/**
 * Aviso de mensagem nova no computador do atendente.
 *
 * O som do painel só existe com a aba aberta. Com o painel minimizado ou atrás de outra
 * janela — WhatsApp Web, ERP, planilha —, o atendente não via nem ouvia nada, e o cliente
 * ficava esperando (pedido de 03/10). Isto cobre esse caso: a notificação do sistema
 * aparece mesmo com o navegador no fundo, e clicar nela traz o painel para a frente.
 *
 * É escolha de cada um e começa desligado: a permissão de notificação só é pedida quando a
 * pessoa liga a chave no menu, que é também o gesto que o navegador exige para perguntar.
 *
 * Tudo aqui falha em silêncio: push é um extra. Navegador sem suporte, permissão negada,
 * servidor sem chave configurada — em qualquer um desses casos o painel continua igual.
 */

const CHAVE_PREFERENCIA = 'mymaxx:aviso-no-computador';

/**
 * A chave VAPID vem em base64url e o navegador quer bytes.
 *
 * O retorno é o ArrayBuffer, e não a view: `applicationServerKey` aceita `BufferSource`, e
 * um `Uint8Array` tipado com `ArrayBufferLike` não encaixa ali.
 */
function paraBytes(base64url: string): ArrayBuffer {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const bruto = atob(base64);
  const bytes = new Uint8Array(bruto.length);
  for (let i = 0; i < bruto.length; i++) bytes[i] = bruto.charCodeAt(i);
  return bytes.buffer;
}

export function suportaAviso(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** O atendente ligou o aviso neste computador? Desligado é o padrão. */
export function avisoLigado(): boolean {
  try {
    return localStorage.getItem(CHAVE_PREFERENCIA) === 'ligado';
  } catch {
    return false;
  }
}

function lembrar(ligado: boolean): void {
  try {
    localStorage.setItem(CHAVE_PREFERENCIA, ligado ? 'ligado' : 'desligado');
  } catch {
    /* sem onde guardar: a escolha vale só nesta sessão */
  }
}

/**
 * Liga o aviso: pede a permissão, registra o service worker e inscreve o computador.
 *
 * Devolve o que de fato aconteceu, porque o menu precisa dizer a verdade ao atendente —
 * "ligado" quando ligou, e o motivo quando não deu.
 */
export async function ligarAviso(): Promise<'ligado' | 'sem-suporte' | 'negado' | 'indisponivel'> {
  if (!suportaAviso()) return 'sem-suporte';
  try {
    const { chave } = await atendimentoApi.chavePush();
    // Sem chave no servidor o push está desligado (falta a configuração do VAPID).
    if (!chave) return 'indisponivel';

    if (Notification.permission === 'denied') return 'negado';
    if (
      Notification.permission === 'default' &&
      (await Notification.requestPermission()) !== 'granted'
    ) {
      return 'negado';
    }

    const registro = await navigator.serviceWorker.register('/push-sw.js');
    await navigator.serviceWorker.ready;
    const inscricao =
      (await registro.pushManager.getSubscription()) ??
      (await registro.pushManager.subscribe({
        // Obrigatório nos navegadores de hoje: todo push precisa virar algo visível. Push
        // silencioso para rastrear alguém não é permitido — e não é o que queremos.
        userVisibleOnly: true,
        applicationServerKey: paraBytes(chave),
      }));

    const json = inscricao.toJSON();
    if (!json.keys?.p256dh || !json.keys.auth) return 'indisponivel';
    await atendimentoApi.inscreverPush({
      endpoint: inscricao.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    });
    lembrar(true);
    return 'ligado';
  } catch {
    return 'indisponivel';
  }
}

/**
 * Desliga o aviso neste computador.
 *
 * Cancela no servidor antes de descartar a inscrição no navegador: na ordem inversa, o
 * endpoint se perderia e o servidor continuaria tentando avisar um computador que não quer
 * mais ser avisado.
 */
export async function desligarAviso(): Promise<void> {
  lembrar(false);
  if (!suportaAviso()) return;
  try {
    const registro = await navigator.serviceWorker.getRegistration('/push-sw.js');
    const inscricao = await registro?.pushManager.getSubscription();
    if (inscricao) {
      await atendimentoApi.cancelarPush(inscricao.endpoint).catch(() => undefined);
      await inscricao.unsubscribe().catch(() => undefined);
    }
  } catch {
    /* já estava desligado, ou o navegador não deixou: a preferência local basta */
  }
}

/**
 * Reinscreve no login, quando a pessoa já tinha ligado o aviso neste computador.
 *
 * A inscrição vive no navegador e é por pessoa no servidor: num posto compartilhado, quem
 * entra agora precisa receber os avisos dele, e não os de quem usou o computador antes.
 * Não pede permissão nenhuma — se ela não existir mais, sai calado.
 */
export async function reinscreverSeLigado(): Promise<void> {
  if (!avisoLigado() || !suportaAviso()) return;
  if (Notification.permission !== 'granted') return;
  await ligarAviso();
}

/**
 * Mantém a inscrição deste computador de pé enquanto houver alguém logado.
 *
 * Mora nas molduras, junto do batimento de presença, porque a inscrição é por pessoa no
 * servidor: num posto compartilhado, quem entra agora precisa receber os avisos dele, e não
 * os de quem usou o computador no turno anterior.
 */
export function useAvisoNoComputador(): void {
  const usuarioId = useSessao((e) => e.usuario?.id);

  useEffect(() => {
    if (!usuarioId) return;
    void reinscreverSeLigado();
  }, [usuarioId]);
}
